import { ArrowRight, Briefcase, Car, CheckCircle, CreditCard, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import PublicShell from '@/components/layouts/PublicShell';

const FinanzierungenPage = () => {
  return (
    <PublicShell>
      {/* Hero Section - Exact Height */}
      <div className="relative" style={{height: '70vh'}}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{backgroundImage: 'url(/images/sv-team-meeting.jpg)'}}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/75 via-slate-800/60 to-transparent"></div>

        <div className="relative z-10 h-full flex items-center px-16">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-light text-white mb-8 leading-tight font-serif">
              Finanzierungen
            </h1>

            <p className="text-xl md:text-2xl text-slate-200 leading-relaxed mb-10 max-w-3xl">
              Lösungen für private und berufliche Vorhaben - Ihre Träume finanzieren wir mit
            </p>
          </div>
        </div>
      </div>

      {/* Green Section */}
      <div className="bg-primary-800">
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* Left Side - Text (3 columns) */}
          <div className="lg:col-span-3 p-12 lg:p-20 flex items-center">
            <div className="max-w-2xl">
              <div className="w-16 h-16 mb-8">
                <CreditCard className="w-16 h-16 text-white" />
              </div>

              <h2 className="text-4xl md:text-5xl font-light text-white mb-8 leading-tight font-serif">
                Unsere <em className="italic">Finanzierungslösungen</em>
              </h2>

              <p className="text-xl text-primary-100 leading-relaxed">
                Von der Immobilienfinanzierung bis zum Geschäftskredit - wir finden die passende Lösung für Sie.
                Durch unsere Partnerschaften mit über 400 Banken finden wir die besten Konditionen.
              </p>
            </div>
          </div>

          {/* Right Side - Image (2 columns) */}
          <div className="lg:col-span-2 relative min-h-96">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{backgroundImage: 'url(/images/sv-handshake.jpg)'}}
            ></div>
          </div>
        </div>
      </div>

      {/* Four White Cards Section - Financing Types */}
      <div className="relative -mt-16 z-20">
        <div className="max-w-[1720px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
          {/* Single Large White Box */}
          <div className="bg-white rounded-none shadow-[0_8px_40px_rgba(16,24,40,0.08)] w-full mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-gray-200">
              {/* Card 1 - Immobilienfinanzierung */}
              <div className="px-6 sm:px-8 md:px-10 lg:px-6 xl:px-10 py-12 text-left min-w-0">
                <div className="w-12 h-12 bg-primary-500 rounded-none flex items-center justify-center mb-6">
                  <Home className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-4 leading-tight">Immobilienfinanzierung</h3>
                <p className="text-[14px] text-slate-600 leading-relaxed">
                  Eigenheim, Eigentumswohnung oder Anlageobjekt - wir finanzieren Ihre Immobilie
                  zu optimalen Konditionen.
                </p>
              </div>

              {/* Card 2 - Autofinanzierung */}
              <div className="px-6 sm:px-8 md:px-10 lg:px-6 xl:px-10 py-12 text-left min-w-0">
                <div className="w-12 h-12 bg-red-500 rounded-none flex items-center justify-center mb-6">
                  <Car className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-4 leading-tight">Autofinanzierung</h3>
                <p className="text-[14px] text-slate-600 leading-relaxed">
                  Neuwagen, Gebrauchtwagen oder Motorrad - flexible Finanzierung
                  für Ihr Fahrzeug mit attraktiven Zinsen.
                </p>
              </div>

              {/* Card 3 - Unternehmensfinanzierung */}
              <div className="px-6 sm:px-8 md:px-10 lg:px-6 xl:px-10 py-12 text-left min-w-0">
                <div className="w-12 h-12 bg-gold-500 rounded-none flex items-center justify-center mb-6">
                  <Briefcase className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-base font-bold text-slate-800 mb-4 leading-tight break-words">Unternehmensfinanzierung</h3>
                <p className="text-[14px] text-slate-600 leading-relaxed">
                  Existenzgründung, Betriebsmittel oder Investitionen -
                  wir unterstützen Ihr Business mit maßgeschneiderten Lösungen.
                </p>
              </div>

              {/* Card 4 - Privatkredit */}
              <div className="px-6 sm:px-8 md:px-10 lg:px-6 xl:px-10 py-12 text-left min-w-0">
                <div className="w-12 h-12 bg-gold-500 rounded-none flex items-center justify-center mb-6">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-4 leading-tight">Privatkredit</h3>
                <p className="text-[14px] text-slate-600 leading-relaxed">
                  Renovierung, Urlaub oder andere Wünsche -
                  flexible Privatkredite zu fairen Konditionen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Primary Section - Benefits */}
      <div className="bg-primary-900 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-5">
          {/* Left Side - Text (3 columns) */}
          <div className="lg:col-span-3 p-12 lg:p-20 flex items-center">
            <div className="max-w-2xl">
              <div className="w-16 h-16 mb-8">
                <CheckCircle className="w-16 h-16 text-white" />
              </div>

              <h2 className="text-4xl md:text-5xl font-light text-gold-400 mb-8 leading-tight font-serif">
                Ihre Vorteile <em className="italic">bei uns</em>
              </h2>

              <p className="text-xl text-primary-100 leading-relaxed">
                Günstige Konditionen durch 400+ Banken, schnelle digitale Bearbeitung und persönliche Beratung
                von der Planung bis zur Auszahlung.
              </p>
            </div>
          </div>

          {/* Right Side - Image (2 columns) */}
          <div className="lg:col-span-2 relative min-h-96">
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{backgroundImage: 'url(/images/sv-group-meeting.jpg)'}}
            ></div>
          </div>
        </div>
      </div>

      {/* Three White Cards Section - Benefits */}
      <div className="relative -mt-16 z-20">
        <div className="max-w-[1720px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24">
          {/* Single Large White Box */}
          <div className="bg-white rounded-none shadow-[0_8px_40px_rgba(16,24,40,0.08)] w-full mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">
              {/* Card 1 - Günstige Konditionen */}
              <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24 py-12 text-left">
                <h3 className="text-xl font-bold text-slate-800 mb-6 leading-tight">Günstige Konditionen</h3>
                <p className="text-[16px] text-slate-600 leading-relaxed">
                  Durch unsere Partnerschaften mit über 400 Banken finden wir die besten Konditionen für Sie.
                  Wir vergleichen alle Angebote und wählen das optimale für Ihre Situation aus.
                </p>
              </div>

              {/* Card 2 - Schnelle Bearbeitung */}
              <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24 py-12 text-left">
                <h3 className="text-xl font-bold text-slate-800 mb-6 leading-tight">Schnelle Bearbeitung</h3>
                <p className="text-[16px] text-slate-600 leading-relaxed">
                  Digitale Antragsstrecke und schnelle Prüfung - oft schon innerhalb von 24 Stunden.
                  Unsere modernen Prozesse sparen Ihnen Zeit und beschleunigen die Finanzierung.
                </p>
              </div>

              {/* Card 3 - Persönliche Beratung */}
              <div className="px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24 py-12 text-left">
                <h3 className="text-xl font-bold text-slate-800 mb-6 leading-tight">Persönliche Beratung</h3>
                <p className="text-[16px] text-slate-600 leading-relaxed">
                  Individuelle Beratung von der Planung bis zur Auszahlung - wir sind für Sie da.
                  Unsere Experten begleiten Sie durch den gesamten Finanzierungsprozess.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section - Blue */}
      <div className="bg-primary-600 py-16">
        <div className="max-w-[1720px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-24 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Bereit für Ihre Finanzierung?
            </h2>
            <p className="text-xl text-primary-100 mb-8 leading-relaxed">
              Lassen Sie uns gemeinsam die optimale Finanzierungslösung für Ihr Vorhaben finden.
              Kostenlos und unverbindlich.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-none shadow-lg border border-gray-200">
                <span className="text-primary-600 font-bold">Jetzt Finanzierung anfragen</span>
                <ArrowRight className="ml-2 w-5 h-5 text-primary-600" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </PublicShell>
  );
};

export default FinanzierungenPage;
